import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';

import { details as feature } from '../../../featureConfig';
import { getPropertyPdfLink } from '../../../ajax/endpoints';
import { getPropertyImages } from '../../../utils/attachmentUtils';
import { activateModal } from '../../../store/actions';
import { ViewerPrimaryColorUserStyle } from '../../UserCustomization';
import ConditionalWrapper from '../../ConditionalWrapper/ConditionalWrapper';

import svgInline from '../../SvgInline/SvgInline';
import folderIcon from '../../List/icon-folder.svg';

import reportsIcon from '../../../shared/assets/report-icon.png';
import specialReportIconDefault from '../../../shared/assets/special-reports.png';
import specialReportIconDemographics from '../../../shared/assets/icon-demographics.png';
import videosIcon from '../../../shared/assets/icon-videos.png';
import photosIcon from '../../../shared/assets/icon-photos.png';
import printIcon from '../../../shared/assets/icon-print.png';
import filesIcon from '../../../shared/assets/icon-attachment.png';
import shareIcon from '../../../shared/assets/icon-mail.png';
import tourIcon from '../../../shared/assets/icon-360-degrees.png';
import workforceIcon from '../../../shared/assets/icon-workforce.svg';
import streetviewIcon from './icon-street-view-greyscale.png';

import ShareModal from '../../Modal/ShareModal/ShareModal';
import DownloadModal from '../../Modal/DownloadModal/DownloadModal';
import SpecialAttachmentsDownloadModal from '../../Modal/DownloadModal/SpecialAttachmentsDownloadModal';
import VideoModal from '../../Modal/VideoModal/VideoModal';

import { storage } from '../../../config';

import './PropertyDetailHeader.css';

export function PropertyDetailHeaderComponent({
  basicInfo,
  id,
  organizationId,
  property,
  showSlideshow,
  type = '',
  showShareModal,
  handleModalOpen,
  onDemographicsClick,
  viewer,
  onModalClick,
  onAddToFavoritesClick,
  isFavorite,
  item,
}) {
  const images = property ? getPropertyImages(property, type) : [];
  const imagesLength = images.length;
  const attachments = (property.attachments) ? property.attachments.attachments || [] : [];
  const specialAttachments = (property.attachments) ? property.attachments.specialAttachments || [] : []; // eslint-disable-line max-len
  const videos = (property.attachments) ? property.attachments.videos || [] : [];
  const videoLength = videos.length;
  const { name } = basicInfo;
  const pdfLink = getPropertyPdfLink(
    type.slice(0, -1),
    organizationId,
    id,
    property.appendAttachments,
  );
  const overviewSectionsToShow = viewer.buildingSettings.overviewSectionsToShow;
  const modalType = 'folders';

  const showESRIReports = !!viewer.adminSettings.showESRIReports;
  const showWorkforceReports = !!viewer.adminSettings.lightcastWorkforceData;
  const specialReportsLabel = viewer.adminSettings.specialCommAttachmentLabel !== '' ? viewer.adminSettings.specialCommAttachmentLabel : 'Specific Reports';
  let specialReportsIconSelected = specialReportIconDefault;
  if (viewer.adminSettings.specialCommAttachmentIconSelected === 0) {
    specialReportsIconSelected = specialReportIconDefault;
  } else if (viewer.adminSettings.specialCommAttachmentIconSelected === 1) {
    specialReportsIconSelected = specialReportIconDemographics;
  } else if (viewer.adminSettings.specialCommAttachmentIconSelected === 2 && viewer.adminSettings.specialCommAttachmentIcon !== '') {
    specialReportsIconSelected = `${storage.cloudStoragePath}/${viewer.adminSettings.specialCommAttachmentIcon}`;
  }

  const tour360Url = (property.attachments && property.attachments.tourUrl) || null;
  const coordinates = _.get(property, 'basicInfo.location.geoLocation.coordinates');

  return (
    <div className="detail-header">
      <div className="detail-header-left">
        <h1>{name}</h1>
      </div>
      <div className="detail-header-right">
        <ShareModal />
        <DownloadModal files={attachments} />
        <ConditionalWrapper condition={specialAttachments.length > 0}>
          <SpecialAttachmentsDownloadModal
            modalHeader={specialReportsLabel}
            files={specialAttachments}
          />
        </ConditionalWrapper>
        <ul className="property-toolbar">
          {coordinates &&
            <li data-tip="View in Google Street View" className="property-detail-street-view">
              <a href={`https://maps.google.com/maps?q=&layer=c&cbll=${coordinates[1]},${coordinates[0]}&cbp=11,0,0,0,0`} target="_blank" rel="noopener noreferrer" className="street-view">
                <img className="street-view-img" src={streetviewIcon} alt="Link to Google Street View" />
                <div>Street View</div>
              </a>
            </li>
          }
          <li data-tip="Add/Remove From Folders" className={isFavorite ? 'toolbar-folders is-favorite' : 'toolbar-folders'}>
            <a
              href=""
              onClick={e => {
                e.preventDefault();
                handleModalOpen({
                  properties: [item],
                  propertyType: type,
                  type: modalType,
                  action: isFavorite ? 'remove' : 'save',
                });
                onAddToFavoritesClick();
              }}
            >
              {svgInline(folderIcon)}
              <div>Manage Folders</div>
            </a>
          </li>

          <ConditionalWrapper condition={specialAttachments.length > 0}>
            <li className="badged">
              <a
                href={specialAttachments.length === 0 ? '#' : ''}
                onClick={e => { e.preventDefault(); if (specialAttachments.length) { onModalClick('specialDownload', [property]); } }}
              ><img src={specialReportsIconSelected} alt="" /><div>{specialReportsLabel}</div></a>
              <ConditionalWrapper condition={specialAttachments.length >= 0}>
                <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
                  <span className="badge">{specialAttachments.length}</span>
                </ViewerPrimaryColorUserStyle>
              </ConditionalWrapper>
            </li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={showESRIReports}>
            <li data-tip="ESRI Reports"><a
              href=""
              onClick={e => {
                e.preventDefault();
                handleModalOpen({ properties: [property], type: 'report' });
                onDemographicsClick();
              }}
            >
              <img src={reportsIcon} alt="" /><div>ESRI Reports</div>
            </a></li>
          </ConditionalWrapper>
          {showWorkforceReports && (
            <li data-tip="Workforce Reports">
              <a
                href=""
                onClick={e => {
                  e.preventDefault();
                  onModalClick('workforce-report', [property]);
                }}
              >
                {svgInline(workforceIcon)}<div>Workforce Reports</div>
              </a>
            </li>
          )}
          <ConditionalWrapper condition={feature.videos && (!overviewSectionsToShow || overviewSectionsToShow.includes('Videos'))}>
            <li className="badged">
              <a
                href={videoLength === 0 ? '#' : ''}
                onClick={e => {
                  e.preventDefault();
                  return videoLength !== 0 ? onModalClick(`video-header-${property._id}`, [property]) : '';
                }}
              ><img src={videosIcon} alt="" /><div>Videos</div></a>
              <VideoModal id={property._id} urlList={videos} modalName={'video-header'} />
              <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
                <span className="badge">{videoLength}</span>
              </ViewerPrimaryColorUserStyle>
            </li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={feature.photos && (!overviewSectionsToShow || overviewSectionsToShow.includes('Photos'))}>
            <li className="badged">
              <a
                href={imagesLength === 0 ? '#' : ''}
                onClick={e => {
                  e.preventDefault();
                  if (imagesLength) {
                    showSlideshow(property._id);
                  }
                }}
              >
                <img src={photosIcon} alt="" />
                <div>Photos</div>
              </a>
              <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
                <span className="badge">{imagesLength}</span>
              </ViewerPrimaryColorUserStyle>
            </li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={tour360Url}>
            <li><a
              href={tour360Url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={tourIcon} alt="360 Tour" className="link-tour" /><div>360 Tour</div>
            </a></li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={feature.print && (!overviewSectionsToShow || overviewSectionsToShow.includes('Brochure'))}>
            <li><a target="_blank" rel="noopener noreferrer" href={pdfLink}><img src={printIcon} alt="" /><div>Print Brochure</div></a></li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={feature.files && (!overviewSectionsToShow || overviewSectionsToShow.includes('Files'))}>
            <li className="badged">
              <a
                href={attachments.length === 0 ? '#' : ''}
                onClick={e => { e.preventDefault(); if (attachments.length) { onModalClick('download', [property]); } }}
              ><img src={filesIcon} alt="" /><div>Files</div></a>
              <ConditionalWrapper condition={attachments.length >= 0}>
                <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
                  <span className="badge">{attachments.length}</span>
                </ViewerPrimaryColorUserStyle>
              </ConditionalWrapper>
            </li>
          </ConditionalWrapper>
          <ConditionalWrapper condition={feature.share && (!overviewSectionsToShow || overviewSectionsToShow.includes('Share'))}>
            <li><a tabIndex="0" onClick={showShareModal}><img src={shareIcon} alt="" /><div>Share</div></a></li>
          </ConditionalWrapper>
        </ul>
      </div>
    </div>
  );
}

// ToDo: Create a building proptype and import
PropertyDetailHeaderComponent.propTypes = {
  basicInfo: PropTypes.object,
  id: PropTypes.string,
  organizationId: PropTypes.string,
  type: PropTypes.string,
  property: PropTypes.object,
  showSlideshow: PropTypes.func,
  showShareModal: PropTypes.func,
  handleModalOpen: PropTypes.func,
  onDemographicsClick: PropTypes.func,
  viewer: PropTypes.object.isRequired,
  onModalClick: PropTypes.func.isRequired,
  onAddToFavoritesClick: PropTypes.func,
  isFavorite: PropTypes.bool,
  item: PropTypes.object,
};

/* istanbul-ignore-next-line */
function mapStateToProps(state, { type, property }) {
  return {
    item: state[type].items.find(item => item._id === property._id),
    isFavorite: state.favorites.folders.savedFolders
      .some(folder => (folder[type] && folder[type].indexOf(property._id) !== -1)),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onModalClick: (modal, properties) => { dispatch(activateModal(modal, properties)); },
    activateFavoriteModal: () => { dispatch(activateModal('folders')); },
  };
}

function mergeProps(propsFromState, propsFromDispatch, ownProps) {
  return {
    onAddToFavoritesClick: () => {
      propsFromDispatch.activateFavoriteModal('folders');
    },
    ...propsFromDispatch,
    ...propsFromState,
    ...ownProps,
  };
}

export default
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(PropertyDetailHeaderComponent);



// WEBPACK FOOTER //
// ./src/components/PropertyDetail/PropertyDetailHeader/PropertyDetailHeader.js